<template>
  <div class="position-relative">
    <div v-if="field.safe" class="float-left">
      <button
        v-if="isLock()"
        type="button"
        class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
        @click="unlockField"
      >
        <i class="material-icons">lock</i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
        @click="lockField"
      >
        <i class="material-icons">lock_open</i>
      </button>
    </div>
    <!-- Campo select -->
    <select
      v-if="field.type === 'select'"
      :id="fieldId"
      class="form-select"
      :class="{ 'calculated-field': isLock(), 'readonly-field': field.readonly }"
      :value="value"
      @change="onFieldChange($event.target.value)"
    >
      <option value="">Seleccione...</option>
      <option v-for="opt in field.options" :key="opt.value" :value="opt.value">
        {{ opt.label }}
      </option>
    </select>

    <!-- Campo de texto (+ traducciones como siblings name_es, …) -->
    <template
      v-else-if="field.type === 'string' || field.type === 'text'"
    >
      <input
        :value="baseDisplayValue"
        :id="fieldId"
        class="form-control"
        :class="{
          'calculated-field': isLock(),
          'readonly-field': field.readonly,
        }"
        style="background: none"
        :placeholder="getPlaceholder()"
        type="text"
        @blur="onFieldBlurChange($event.target.value)"
        @focus="onFieldFocus"
      />
      <div v-if="isTranslatableField" class="field-text-i18n mt-2">
        <div
          v-for="code in translatableLocales"
          :key="'fi18n-' + field.key + '-' + code"
          class="mb-2"
        >
          <label class="small text-muted mb-0" :for="fieldId + '-' + code">
            {{ languageNameForCode(code) }}
          </label>
          <input
            :id="fieldId + '-' + code"
            class="form-control"
            :class="{
              'calculated-field': isLock(),
              'readonly-field': field.readonly,
            }"
            style="background: none"
            :placeholder="getPlaceholder()"
            type="text"
            :value="localeSiblingValue(code)"
            @blur="onLocaleSiblingBlur(code, $event.target.value)"
            @focus="onFieldFocus"
          />
        </div>
      </div>
    </template>

    <!-- Campo de email -->
    <input
      v-else-if="field.type === 'email'"
      :value="value"
      :id="fieldId"
      class="form-control"
      :class="{
        'calculated-field': isLock(),
        'readonly-field': field.readonly,
      }"
      style="background: none"
      :placeholder="getPlaceholder()"
      type="email"
      @blur="onFieldBlurChange($event.target.value)"
      @focus="onFieldFocus"
    />

    <!-- Campo de área de texto (+ traducciones siblings) -->
    <template v-else-if="field.type === 'textarea'">
      <textarea
        :value="baseDisplayValue"
        :id="fieldId"
        class="form-control"
        :class="{
          'readonly-field': field.readonly,
        }"
        style="background: none"
        :placeholder="getPlaceholder()"
        rows="4"
        cols="50"
        @blur="onFieldBlurChange($event.target.value)"
        @focus="onFieldFocus"
      ></textarea>
      <div v-if="isTranslatableField" class="field-text-i18n mt-2">
        <div
          v-for="code in translatableLocales"
          :key="'fi18n-ta-' + field.key + '-' + code"
          class="mb-2"
        >
          <label class="small text-muted mb-0" :for="fieldId + '-' + code">
            {{ languageNameForCode(code) }}
          </label>
          <textarea
            :id="fieldId + '-' + code"
            class="form-control"
            :class="{ 'readonly-field': field.readonly }"
            style="background: none"
            :placeholder="getPlaceholder()"
            rows="3"
            :value="localeSiblingValue(code)"
            @blur="onLocaleSiblingBlur(code, $event.target.value)"
            @focus="onFieldFocus"
          ></textarea>
        </div>
      </div>
    </template>

    <!-- Campo de fecha -->
    <InputDate
      v-else-if="field.type === 'date' && !field.isArray"
      :field="field"
      :value="value"
      :isLock="isLock"
      @input="onFieldChange"
      @blur="onFieldBlur"
      @focus="onFieldFocus"
    />

    <!-- Campo numérico -->
    <div v-else-if="field.type === 'number'" class="position-relative">
      <input
        type="float"
        :value="value"
        :id="fieldId"
        class="form-control field-number"
        :class="{
          'calculated-field': isLock(),
          'readonly-field': field.readonly,
          'currency-field': field.currency,
        }"
        style="background: none"
        :placeholder="getPlaceholder()"
        @input="onNumberInput($event)"
        @change="onFieldChange($event.target.value)"
        @blur="onFieldBlur"
        @focus="onFieldFocus"
      />
    </div>

    <!-- Campo booleano -->
    <div v-else-if="field.type === 'boolean'" class="form-check form-switch">
      <input
        type="checkbox"
        :checked="value === true"
        :id="fieldId"
        class="form-check-input bg-primary border-primary"
        :disabled="isLock()"
        @change="onFieldChange($event.target.checked)"
      />
      <label :for="fieldId" class="form-check-label">
        {{ value === true ? "YES" : "NO" }}
      </label>
    </div>

    <!-- List: componente modularizado -->
    <InputList
      v-else-if="field.type === 'list'"
      :field="field"
      :value="value"
      :fieldId="fieldId"
      :listOptions="listOptions"
      :disabled="isLock() || field.readonly"
      @input="onFieldChange"
    />

    <!-- Campo textlist: select con opciones personalizadas -->
    <select
      v-else-if="field.type === 'textlist'"
      :id="fieldId"
      :value="value"
      class="form-select"
      @change="onFieldChange($event.target.value)"
    >
      <option value="">Select an option</option>
      <option
        v-for="option in textlistOptions"
        :key="option._id"
        :value="option._id"
      >
        {{ option.label }}
      </option>
    </select>

    <!-- Campo select visual de iconos (tipo "icon") -->
    <div v-else-if="field.type === 'icon'">
      <div class="d-flex flex-wrap">
        <span
          v-for="icon in field.icons || defaultIcons"
          :key="icon"
          class="icon-select-option"
          :class="{ selected: value === icon }"
          @click="onFieldChange(icon)"
          style="
            cursor: pointer;
            margin: 4px;
            padding: 6px;
            border-radius: 6px;
            border: 1px solid #eee;
            display: flex;
            align-items: center;
          "
        >
          <i
            class="material-icons"
            :style="{
              fontSize: '28px',
              color: value === icon ? 'var(--bs-primary)' : '#888',
            }"
            >{{ icon }}</i
          >
          <span v-if="value === icon" style="margin-left: 4px; color: var(--bs-primary)"
            >✔</span
          >
        </span>
      </div>
    </div>

    <!-- Campo de archivo -->
    <UploadFile
      v-else-if="field.type === 'file'"
      :id="fieldId"
      :accept="field.accept"
      :required="field.required"
      :value="value"
      @input="onFieldChange"
      class="file-input"
    />

    <!-- Campo tipo botón -->
    <button
      v-else-if="field.type === 'button'"
      type="button"
      :id="fieldId"
      class="btn btn-primary"
      :disabled="isLock()"
      @click="onButtonClick"
    >
      <i class="material-icons text-white" v-if="field.label2">{{
        $t(field.label2)
      }}</i>
      {{ $t(field.buttonLabel || field.label || "Botón") }}
    </button>

    <!-- Botón para abrir vista en popup -->
    <div
      v-else-if="field.view && field.type === 'reference' && field.ref"
      class="d-flex align-items-center gap-2"
    >
      <span
        v-if="value"
        @click="$router.push(`/dynamic-model/shipping/view/${value.parent}`)"
        class="text-primary cursor-pointer"
      >
        <span :class="{ 'text-danger': value.amount - formData.amount < 0 }">
          Amount: {{ value.amount }} | Remaining: {{ value.remaining }}</span
        >
      </span>
      <input
        :value="value"
        :id="fieldId"
        class="form-control d-none"
        :class="{
          'readonly-field': field.readonly,
        }"
        style="background: none"
        :placeholder="field.label"
        type="text"
        @blur="onFieldBlur"
        @focus="onFieldFocus"
      />
      <button
        type="button"
        class="btn btn-outline-primary p-1"
        @click="openViewModal(field)"
        v-if="value"
      >
        Change item <i class="material-icons">visibility</i>
      </button>
      <button
        type="button"
        class="btn btn-outline-primary p-1"
        @click="openViewModal(field)"
        v-else
      >
        Select item <i class="material-icons">visibility</i>
      </button>
    </div>

    <!-- Modal para mostrar vista -->
    <div
      class="modal fade"
      :class="{ show: showViewModal }"
      tabindex="-1"
      v-if="showViewModal"
    >
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ currentViewField?.label || "View" }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeViewModal"
            ></button>
          </div>
          <div class="modal-body">
            <ContentSkeleton v-if="isLoading" variant="page" :rows="6" :cols="5" />
            <div v-else>
              <DynamicModel
                :paramViewId="currentViewField.view"
                :chooseItem="true"
                :multiple="false"
                @chooseItems="onChooseItems($event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UploadFile from "./UploadFile.vue";
import DynamicModel from "../../../pages/gotrading/dynamic-model.vue";
import { InputDate, InputList } from "./inputs";
import { wencesApi } from "@/api/wencesApi";
import {
  coerceBaseFieldString,
  getFieldLocaleSiblingKey,
  getFieldTranslatableLocales,
  isFieldTranslatable,
} from "@/utils/fieldValueI18n";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "FormInput",
  components: {
    ContentSkeleton,
    UploadFile,
    DynamicModel,
    InputDate,
    InputList,
  },
  inject: {
    formContext: { default: null },
  },
  props: {
    formData: {
      type: Object,
      default: () => {},
    },
    field: {
      type: Object,
      required: true,
    },
    lock: {
      type: Boolean,
      default: false,
    },
    popup: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Number, Boolean, Object, Array],
      default: null,
    },
    fieldId: {
      type: String,
      required: true,
    },

    listOptions: {
      type: Array,
      default: () => [],
    },
    textlistOptions: {
      type: Array,
      default: () => [],
    },
    defaultIcons: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showViewModal: false,
      currentViewField: null,
      modelFields: [],
      lists: [],
      models: [],
      selectedModelData: null,
      isLoading: false,
      savevalue: null,
      editingFields: "",
      router: null,
      isRestoringValue: false,
      // Nuevo: campo para rastrear si el usuario está editando activamente
      activelyEditing: false,
    };
  },
  mounted() {
    this.router = this.$router;
  },
  computed: {
    isTranslatableField() {
      return (
        isFieldTranslatable(this.field) && this.translatableLocales.length > 0
      );
    },
    translatableLocales() {
      return getFieldTranslatableLocales(this.field);
    },
    baseDisplayValue() {
      return coerceBaseFieldString(this.value);
    },
  },
  created() {
    // Lógica específica del componente si es necesaria
  },
  methods: {
    languageNameForCode(code) {
      return (
        (this.$getLanguageName && this.$getLanguageName(code)) ||
        String(code).toUpperCase()
      );
    },
    localeSiblingValue(code) {
      const key = getFieldLocaleSiblingKey(this.field.key, code);
      if (this.formContext && typeof this.formContext.getValue === "function") {
        const v = this.formContext.getValue(key);
        return v != null ? String(v) : "";
      }
      if (this.formData && typeof this.formData === "object") {
        const v = this.formData[key];
        return v != null ? String(v) : "";
      }
      return "";
    },
    onLocaleSiblingBlur(code, newValue) {
      this.onFieldBlur();
      const key = getFieldLocaleSiblingKey(this.field.key, code);
      const str = newValue != null ? String(newValue) : "";
      if (this.formContext && typeof this.formContext.setValue === "function") {
        this.activelyEditing = true;
        this.formContext.setValue(key, str);
        return;
      }
      if (this.formData && typeof this.formData === "object") {
        this.$set(this.formData, key, str);
        this.activelyEditing = true;
        this.$emit("input", this.value);
        this.$emit("sibling-input", { locale: code, path: key, value: str });
      }
    },
    isLock() {
      // Si el usuario está editando activamente, no bloquear
      if (this.activelyEditing) {
        return false;
      }

      if (this.field.type === "list" && !this.value) {
        return false;
      }

      if (!this.value) {
        return false;
      }

      if (this.field.safe && this.editingFields !== this.field.key) {
        return true;
      }
      return false;
    },
    onChooseItems(items) {
      const selectedValue = { _id: items[0] };
      this.showViewModal = false;
      this.onFieldChange(selectedValue);
    },
    onNumberInput(event) {
      let value = event.target.value;
      
      // Reemplazar comas con puntos
      value = value.replace(/,/g, '.');
      
      // Filtrar solo números, puntos y signos negativos
      value = value.replace(/[^0-9.-]/g, '');
      
      // Asegurar que solo haya un punto decimal
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Agregar 0 al inicio si empieza con punto
      if (value.startsWith('.')) {
        value = '0' + value;
      }
      
      // Agregar 0 al inicio si empieza con punto después de un signo negativo
      if (value.startsWith('-.')) {
        value = '-0' + value.substring(1);
      }
      
      // Actualizar el valor del input
      event.target.value = value;
      
      // Emitir el cambio
      this.onFieldChange(value);
    },

    onFieldChange(value) {
      // Debug específico solo para total_proformship
      if (this.field && this.field.key === "total_proformship") {
      }

      

      // Marcar que el usuario está editando activamente
      this.activelyEditing = true;

      this.$emit("input", value);
    },

    async openViewModal(field) {
      this.currentViewField = field;
      this.showViewModal = true;
      this.isLoading = true;

      try {
        // Fetch lists
        const { data: lists } = await wencesApi.get("/api/gotrading/lists");
        this.lists = lists;

        // Fetch view data
        const { data: view } = await wencesApi.get(
          `/api/gotrading/views/${field.view}`
        );
      } catch (error) {
        console.error("Error loading view data:", error);
      } finally {
        this.isLoading = false;
      }
    },
    closeViewModal() {
      this.showViewModal = false;
      this.currentViewField = null;
      this.modelFields = [];
    },
    getPlaceholder() {
      return this.field.label;
    },

    onButtonClick() {
      // Emitir un evento cuando se hace clic en el botón
      this.$emit("button-click", {
        field: this.field,
        value: this.field.key,
      });
    },
    // onTagAdded ahora está en InputList.vue

    // Nuevo: método para manejar cuando el campo pierde el foco
    onFieldBlur() {
      // Solo bloquear si el campo es safe y no está en modo edición
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = false;
      }
    },

    // Método para manejar blur y emitir el cambio (usado en campos string/textarea/email)
    onFieldBlurChange(value) {
      // Primero manejar el blur (para el estado de activelyEditing)
      this.onFieldBlur();
      // Luego emitir el cambio del valor (siempre string en campos texto)
      this.onFieldChange(coerceBaseFieldString(value));
    },

    // Nuevo: método para manejar cuando el campo obtiene el foco
    onFieldFocus() {
      // Si el campo es safe y está bloqueado, permitir edición
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = true;
      }
    },

    // Nuevo: método para bloquear manualmente el campo
    lockField() {
      this.editingFields = "";
      this.activelyEditing = false;
    },

    // Nuevo: método para desbloquear manualmente el campo
    unlockField() {
      this.editingFields = this.field.key;
      this.activelyEditing = true;
    },

  },
};
</script>

<style scoped>
.form-control {
  padding: 0 7px !important;
}

.readonly-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
  cursor: not-allowed !important;
  opacity: 0.8;
}

.invalid-field {
  border-color: #dc3545 !important;
}

.readonly-field:focus {
  box-shadow: none !important;
  border-color: #dee2e6 !important;
}

.readonly-field::placeholder {
  color: #adb5bd !important;
}

.modal {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.modal.show {
  display: block;
}

.buttonn-add {
  position: absolute;
  right: 0px;
  top: -27px;
}

.buttonn-edit {
  position: absolute;
  right: 23px;
  top: -27px;
}
</style>

<style>
.group-multiple .buttonn-add {
  display: none;
}

.group-multiple .buttonn-edit {
  display: none;
}

.group-multiple .file-input {
  max-width: 167px;
}
</style>
